import React, {
  useState,
  FormEvent,
  ChangeEvent,
  KeyboardEvent,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
} from "react";
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SpeedIcon from "@mui/icons-material/Speed";
import {
  MenuButtonsProps,
  Message,
  User,
  HcpMentionSendOptions,
  SelectedHcpMention,
} from "../../../types";
import { getMainMenu } from "../../../axios/buttons";
import { MenuDropdown } from "../menu-dropdown/menu-dropdown";
import { MessageTextInput } from "../message-text-input";
import { HcpMentionPicker } from "../hcp-mention-picker/hcp-mention-picker";
import { AttachmentPopup } from "../../attachment-popup/attachment-popup";
import { WhatsAppAudioRecorder } from "../whatsapp-audio-recorder/whatsapp-audio-recorder";
import { useAppStore } from "../../../stores/appStore";
import {
  AppsIcon,
  ChevronUpIcon,
  MicOnIcon,
  PaperclipIcon,
  PauseIcon,
  RoleplaysIcon,
  SendIcon,
  StopIcon,
} from "../../../assets/icon";
import { FooterQuickHints } from "../keyboard-dropdown/keyboard-dropdown";
import { motion } from "framer-motion";
import { useChatStore } from "../../../stores/chatStore";
import { RealtimeSpeechMenu } from "../realtime-speech-menu/realtime-speech-menu";
import { ResponseContextButton } from "../response-context-menu/response-context-button";
import { useDeviceType } from "../../../hooks/useDeviceType";
import { useVoiceToText } from "../../../hooks/useVoiceToText";
import { useTelegramVoiceGesture } from "../../../hooks/useTelegramVoiceGesture";
import { useHcpMentionSearch } from "../../../hooks/useHcpMentionSearch";
import {
  applyPromptPreservingMentions,
  getChatSendValidationMessage,
  hasSendableTextBeyondHcpMentions,
  insertHcpMention,
  mergeMentionsFromResults,
  parseActiveHcpMention,
  rememberHcpSearchLabels,
  toHcpMentionSendOptions,
} from "../../../utils/hcpMentions";
import { createDesktopRequiredSystemMessage } from "../../../helpers/messageHelpers";
import { scrollToBottomJust } from "../../../utils/scrollUtils";
import { playActionSound } from "../../../helpers/actionSound";
import { useMmkStore } from "../../../stores/mmkStore";
import { Lock as LockIcon } from "lucide-react";
import { toast } from "react-toastify";
import { VoiceRecordingPreview } from "./voice-recording-preview/voice-recording-preview";
import { TelegramRecordingOverlay } from "./telegram-recording-overlay/telegram-recording-overlay";
import {
  isBrowserOffline,
  notifyOfflineActionBlocked,
} from "../../../utils/internetConnection";
import {
  lockPillStyle,
  lockedSendButtonSx,
  sendTopActionStyle,
} from "./telegram-recording-overlay/styles";
import { ConversationModePrompt } from "../conversation-mode-prompt/conversation-mode-prompt";
import { MicVolumeHalo } from "../../mic-volume-halo/mic-volume-halo";
import {
  getAttachmentPopupSx,
  getAttachmentWrapperSx,
  getFooterHintsScrollSx,
  getFooterInputRowSx,
  getFooterOuterSx,
  getFooterPinnedBottomSx,
  getFooterShellSx,
  getInputAreaWrapperSx,
  getInputFormSx,
  getInputFormColumnSx,
  getInputControlsRowSx,
  getInputGradientBorderSx,
  getInputInnerSx,
  getInputSlideSx,
  getKeyboardIconButtonSx,
  getKeyboardIconSizeSx,
  getLessonsBadgeProgressSx,
  getLessonsBadgeSx,
  getMenuToggleRowSx,
  getMicButtonSx,
  getRecordingMicButtonSx,
  getTranscribingMicProgressSx,
  getNavTabIconSx,
  getNavTabIconWrapSx,
  getNavTabLabelSx,
  getNavTabSx,
  getPaperclipButtonSx,
  getSendActionContainerSx,
  getSendActionInnerSx,
  getStopButtonSx,
  getTabStripSx,
} from "./styles";

interface MessageFooterProps {
  onSendMessage: (
    command: string,
    createPlaceholder?: boolean,
    hideFromUser?: boolean,
    options?: HcpMentionSendOptions,
  ) => void;
  onRecordAudio?: () => void;
  onFileChange: (
    e: ChangeEvent<HTMLInputElement>,
    type: "image" | "file",
  ) => void;
  isUploading: boolean;
  isUploadingFile: boolean;
  setKeyboardState?: (value: boolean) => void;
  onFeedbackClick?: () => void;
  feedbackDisabled?: boolean;
  // Quiz 3 / socket start_recording — Telegram audio recorder in the input row.
  isAudioRecorderOpen?: boolean;
  onAudioRecorderClose?: () => void;
  sendMessageSocket?: (message: Message) => void;
  currentUser?: User;
  scrollToBottom?: () => void;
}

const bounceVariants: any = {
  hidden: {
    opacity: 0,
    scale: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    scale: [1, 1.1, 1],
    y: [0, -20, 0],
    transition: {
      duration: 0.6,
      times: [0, 0.5, 1],
      ease: "easeInOut",
    },
  },
};

export const MessageFooter = ({
  onSendMessage,
  onFileChange,
  isUploading,
  isUploadingFile,
  setKeyboardState,
  onFeedbackClick: _onFeedbackClick,
  feedbackDisabled: _feedbackDisabled = false,
  isAudioRecorderOpen = false,
  onAudioRecorderClose,
  sendMessageSocket,
  currentUser,
  scrollToBottom,
}: MessageFooterProps) => {
  const {
    showAttachmentsButton,
    isBrowserLocked,
    setIsBrowserLocked,
    messageTheme,
    updateMessageTheme,
    isOnline,
  } = useAppStore();
  const { lessonsToDo, setMessages } = useChatStore();
  const { isQuizInProgress, isFocusSessionInProgress } = useMmkStore();
  const deviceType = useDeviceType();
  const theme = useTheme();
  const [shouldAnimate, setShouldAnimate] = useState(false);

  const [input, setInput] = useState<string>("");
  const [cursor, setCursor] = useState(0);
  const [selectedMentions, setSelectedMentions] = useState<
    SelectedHcpMention[]
  >([]);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionDismissed, setMentionDismissed] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [menuButtons, setMenuButtons] = useState<MenuButtonsProps[]>([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [isRealtimeSpeechMenuOpen, setIsRealtimeSpeechMenuOpen] =
    useState<boolean>(false);
  const [realtimeSpeechMenuAnchorEl, setRealtimeSpeechMenuAnchorEl] =
    useState<HTMLElement | null>(null);

  const realtimeSpeechAnchorRef = useRef<HTMLElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const mentionAnchorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef("");
  const selectedMentionsRef = useRef<SelectedHcpMention[]>([]);
  const clearRecordingRef = useRef<() => void>(() => {});

  const handleTranscribed = useCallback((text: string) => {
    setInput((prev) => {
      const trimmed = prev.trim();
      const next = trimmed ? `${trimmed} ${text}` : text;
      inputRef.current = next;
      return next;
    });
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
  }, []);

  const {
    isRecording,
    isPaused,
    isTranscribing,
    recordedAudioUrl,
    pausedPreviewUrl,
    formattedClockTime,
    recordTimeMs,
    mediaStream: voiceMediaStream,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    clearRecording,
  } = useVoiceToText(handleTranscribed);
  clearRecordingRef.current = clearRecording;
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuToggleRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const inputSlideRef = useRef<HTMLDivElement | null>(null);
  const keyboardToggleRef = useRef<HTMLDivElement | null>(null);
  const skipClearInputOnFocusMount = useRef(true);
  const [isLessonsBadgeLoading, setIsLessonsBadgeLoading] = useState(false);
  const {
    triggerOpenUnreadLessons,
    isPreassessmentOpen,
    isAssessmentOpen,
    focus,
    domain,
  } = useAppStore();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMmkMode = messageTheme === "mmk";
  const isLessonsMode = messageTheme === "lessons";
  const isChatMode = messageTheme === "both" || messageTheme === "chats";
  const isDarkMode = theme.palette.mode === "dark";
  const footerShellBg = isDarkMode ? "rgba(46, 46, 58, 0.98)" : "#E6E0EA";
  // Only the textarea pill is white; this row stays on shell background.
  const footerInputRowBg = "transparent";
  const isOffline = !isOnline || isBrowserOffline();

  const skipMentionDismissResetRef = useRef(false);

  const activeMention = useMemo(
    () => parseActiveHcpMention(input, cursor, selectedMentions),
    [input, cursor, selectedMentions],
  );
  const mentionKey = activeMention
    ? `${activeMention.start}:${activeMention.query}`
    : "";

  useEffect(() => {
    if (skipMentionDismissResetRef.current) {
      skipMentionDismissResetRef.current = false;
      return;
    }
    setMentionDismissed(false);
  }, [mentionKey]);

  const mentionSearchEnabled =
    Boolean(activeMention) &&
    !mentionDismissed &&
    !isMmkMode &&
    !isPreassessmentOpen;

  const { results: mentionResults } = useHcpMentionSearch(
    activeMention?.query ?? "",
    mentionSearchEnabled,
  );

  const mentionPickerOpen = mentionSearchEnabled;

  useEffect(() => {
    setMentionIndex(0);
  }, [mentionResults, activeMention?.query]);

  const voiceDisabled =
    isPreassessmentOpen ||
    isMmkMode ||
    isTranscribing ||
    isBrowserLocked ||
    isOffline ||
    isAudioRecorderOpen;

  const {
    buttonRef: micButtonRef,
    phase: voiceGesturePhase,
    isGestureActive,
    isLocked: isVoiceLocked,
    dragOffsetX,
    lockProgress,
    cancelProgress,
    stopLockedRecording,
    cancelLockedRecording,
    gestureHandlers,
  } = useTelegramVoiceGesture({
    disabled: voiceDisabled,
    onStart: startRecording,
    onStop: stopRecording,
    onCancel: cancelRecording,
  });

  const showVoiceRecordingUi =
    (isRecording || isGestureActive) && !isTranscribing;
  const showRecordingChrome = showVoiceRecordingUi || isAudioRecorderOpen;

  const handleLockedSend = useCallback(() => {
    stopLockedRecording();
  }, [stopLockedRecording]);

  const handleLockedPause = useCallback(() => {
    pauseRecording();
  }, [pauseRecording]);

  const handleLockedResume = useCallback(() => {
    resumeRecording();
  }, [resumeRecording]);

  useEffect(() => {
    inputRef.current = input;
  }, [input]);

  useEffect(() => {
    selectedMentionsRef.current = selectedMentions;
  }, [selectedMentions]);

  type BottomNavTab = "lessons" | "chat" | "mmk";

  const activeNavTab: BottomNavTab = isLessonsMode
    ? "lessons"
    : isChatMode
      ? "chat"
      : "mmk";

  // Single brand purple for all tab icons; inactive is the same hue, softer.
  const navPurple = useMemo(
    () => ({
      active: isDarkMode ? "#C4B5FD" : "#6D28D9",
      inactive: isDarkMode
        ? "rgba(196, 181, 253, 0.42)"
        : "rgba(109, 40, 217, 0.38)",
    }),
    [isDarkMode],
  );

  const navItemColor = (selected: boolean) =>
    selected ? navPurple.active : navPurple.inactive;

  const tabStripBackground = useMemo(() => {
    if (isDarkMode) {
      if (activeNavTab === "lessons") return "#1e2024";
      if (activeNavTab === "chat") return "#1e2126";
      return "#222024";
    }
    if (activeNavTab === "lessons") return "#eef0f4";
    if (activeNavTab === "chat") return "#eef3f6";
    return "#f4f1f5";
  }, [isDarkMode, activeNavTab]);

  const tabDisabled =
    isQuizInProgress || isFocusSessionInProgress || isBrowserLocked;

  const guardOnlineAction = () => {
    if (isOffline) {
      notifyOfflineActionBlocked();
      return false;
    }
    return true;
  };

  const handleLessonsBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!guardOnlineAction()) return;
    if (isPreassessmentOpen || isAssessmentOpen || isLessonsBadgeLoading) {
      return;
    }
    setIsLessonsBadgeLoading(true);
    triggerOpenUnreadLessons();
    window.setTimeout(() => setIsLessonsBadgeLoading(false), 800);
  };

  useEffect(() => {
    setKeyboardState?.(false);
  }, [setKeyboardState]);

  useEffect(() => {
    if (skipClearInputOnFocusMount.current) {
      skipClearInputOnFocusMount.current = false;
      return;
    }
    setInput("");
    setSelectedMentions([]);
    selectedMentionsRef.current = [];
  }, [focus, domain]);

  useEffect(() => {
    if (isPreassessmentOpen || isAssessmentOpen) {
      bounceVariants.hover = undefined;
    } else {
      bounceVariants.hover = {
        scale: 1.5,
        transition: {
          duration: 0.2,
        },
      };
    }
  }, [isPreassessmentOpen, isAssessmentOpen]);

  useEffect(() => {
    const clearControlsStorage = () => {
      sessionStorage.removeItem("controlsManualToggled");
    };
    window.addEventListener("beforeunload", clearControlsStorage);
    return () =>
      window.removeEventListener("beforeunload", clearControlsStorage);
  }, []);

  useEffect(() => {
    const handleOpenRealtimeMenu = () => {
      const tryOpenMenu = (attempts = 0) => {
        if (menuToggleRef.current) {
          setRealtimeSpeechMenuAnchorEl(menuToggleRef.current);
          setIsRealtimeSpeechMenuOpen(true);
        } else if (attempts < 5) {
          // Retry up to 5 times with increasing delays
          setTimeout(() => tryOpenMenu(attempts + 1), 200 * (attempts + 1));
        }
      };
      tryOpenMenu();
    };

    window.addEventListener(
      "openRealtimeMenu",
      handleOpenRealtimeMenu as EventListener,
    );
    return () => {
      window.removeEventListener(
        "openRealtimeMenu",
        handleOpenRealtimeMenu as EventListener,
      );
    };
  }, []);

  useEffect(() => {
    if (isMenuOpen && menuToggleRef.current) {
      setMenuAnchorEl(menuToggleRef.current);
    }
  }, [isMenuOpen]);

  const ROLEPLAY_COMMAND = "/open_roleplay";

  useEffect(() => {
    if (isMenuOpen) {
      getMainMenu()
        .then((data: MenuButtonsProps[]) => {
          const filteredMenu = data.filter((item) => {
            const text = (item.text || "").toLowerCase();
            const command = (item.command || "").toLowerCase();
            return !text.includes("feedback") && !command.includes("feedback");
          });
          const roleplayMenuItem: MenuButtonsProps = {
            text: "Roleplays",
            description: "Start a Roleplay",
            command: ROLEPLAY_COMMAND,
            image: "",
            Icon: RoleplaysIcon,
          };
          const withRoleplay = [roleplayMenuItem, ...filteredMenu];
          setMenuButtons(withRoleplay);
        })
        .catch((error) => console.error("Failed to load menu:", error));
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (isRealtimeSpeechMenuOpen && menuToggleRef.current) {
      setRealtimeSpeechMenuAnchorEl(menuToggleRef.current);
    }
  }, [isRealtimeSpeechMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        menuToggleRef.current &&
        !menuToggleRef.current.contains(target)
      ) {
        setIsMenuOpen(false);
        setMenuAnchorEl(null);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        popupRef.current &&
        !popupRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        !(event.target as HTMLElement).closest("input[type='file']")
      ) {
        setIsPopupOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldAnimate(true);
    }, 250);

    return () => clearTimeout(timer);
  }, []);

  const syncTextareaCursor = (target: HTMLTextAreaElement) => {
    setCursor(target.selectionStart ?? target.value.length);
  };

  // Fixed-height footer input only shows one line; when text wraps (e.g. `@`
  // at the end), keep the caret line visible instead of leaving it scrolled away.
  const scrollTextareaCaretIntoView = useCallback(
    (el: HTMLTextAreaElement | null = textareaRef.current) => {
      if (!el) return;
      const caret = el.selectionStart ?? 0;
      if (caret < el.value.length) return;
      el.scrollTop = el.scrollHeight;
    },
    [],
  );

  useLayoutEffect(() => {
    scrollTextareaCaretIntoView();
  }, [input, cursor, scrollTextareaCaretIntoView]);

  const handleSelectMention = useCallback(
    (result: (typeof mentionResults)[number]) => {
      if (!activeMention) return;

      const next = insertHcpMention(
        input,
        activeMention.start,
        cursor,
        result.label,
      );
      rememberHcpSearchLabels([result]);
      setInput(next.text);
      setCursor(next.cursor);
      setSelectedMentions((prev) => {
        const withoutSame = prev.filter(
          (item) => item.mentionToken !== result.mentionToken,
        );
        return [
          ...withoutSame,
          { mentionToken: result.mentionToken, label: result.label },
        ];
      });
      skipMentionDismissResetRef.current = true;
      setMentionDismissed(true);

      if (result.duplicateAssignment) {
        toast.info(
          "This HCP has more than one assignment. The selected record will be used.",
        );
      }

      requestAnimationFrame(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.focus();
        textarea.setSelectionRange(next.cursor, next.cursor);
        scrollTextareaCaretIntoView(textarea);
      });
    },
    [activeMention, cursor, input, scrollTextareaCaretIntoView],
  );

  const sendWithMode = useCallback(
    async (useHistory: boolean) => {
      if (isPreassessmentOpen || isMmkMode || isBrowserLocked) return;
      if (isOffline) {
        notifyOfflineActionBlocked();
        return;
      }
      if (isRecording || isTranscribing || showVoiceRecordingUi) return;

      const trimmed = input.trim();
      if (!trimmed || !hasSendableTextBeyondHcpMentions(trimmed)) return;

      const mentions = mergeMentionsFromResults(
        trimmed,
        selectedMentions,
        mentionResults,
      );
      const mentionOptions = {
        ...toHcpMentionSendOptions(trimmed, mentions),
        useHistory,
      };

      await setIsBrowserLocked(true);
      onSendMessage(trimmed, true, false, mentionOptions);
      setInput("");
      setSelectedMentions([]);
      selectedMentionsRef.current = [];
      clearRecording();
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    },
    [
      clearRecording,
      input,
      isBrowserLocked,
      isMmkMode,
      isOffline,
      isPreassessmentOpen,
      isRecording,
      isTranscribing,
      mentionResults,
      onSendMessage,
      selectedMentions,
      setIsBrowserLocked,
      showVoiceRecordingUi,
    ],
  );

  const readyToSend =
    hasSendableTextBeyondHcpMentions(input) &&
    !isRecording &&
    !isTranscribing &&
    !showVoiceRecordingUi &&
    !isBrowserLocked &&
    !isMmkMode &&
    !isPreassessmentOpen &&
    !isOffline;

  const chatSendValidationMessage = getChatSendValidationMessage(input);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const handleKeyDown = async (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionPickerOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (mentionResults.length) {
          setMentionIndex((index) => (index + 1) % mentionResults.length);
        }
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (mentionResults.length) {
          setMentionIndex(
            (index) =>
              (index - 1 + mentionResults.length) % mentionResults.length,
          );
        }
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionDismissed(true);
        return;
      }
      if (
        (e.key === "Enter" || e.key === "Tab") &&
        mentionResults.length > 0 &&
        !e.shiftKey
      ) {
        e.preventDefault();
        const selected = mentionResults[mentionIndex] ?? mentionResults[0];
        if (selected) handleSelectMention(selected);
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      if (isMmkMode || isBrowserLocked) {
        e.preventDefault();
        return;
      }
    }
  };

  const handleInput = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "";
      textareaRef.current.style.overflowY = "auto";
      scrollTextareaCaretIntoView(textareaRef.current);
    }
  };

  const handleFileSelection = (
    e: ChangeEvent<HTMLInputElement>,
    type: "image" | "file",
  ) => {
    try {
      onFileChange(e, type);
      setIsPopupOpen(false);
    } catch (error) {
      console.error("Error during file upload:", error);
    }
  };

  const toggleMenu = () => {
    if (isPreassessmentOpen) return;

    if (!isMenuOpen && menuToggleRef.current) {
      sessionStorage.setItem("controlsManualToggled", "true");
      setMenuAnchorEl(menuToggleRef.current);
      setIsMenuOpen(true);
    } else {
      setIsMenuOpen(false);
      setMenuAnchorEl(null);
    }
  };

  const handleCloseRealtimeSpeechMenu = () => {
    setIsRealtimeSpeechMenuOpen(false);
    setRealtimeSpeechMenuAnchorEl(null);
  };

  const handleRealtimeSpeechClick = async () => {
    if (isPreassessmentOpen) return;
    if (isOffline) {
      notifyOfflineActionBlocked();
      return;
    }

    if (deviceType !== "desktop") {
      await playActionSound();

      const systemMessage = createDesktopRequiredSystemMessage();
      setMessages((prev: Message[]) => [...prev, systemMessage]);

      await new Promise((resolve) => setTimeout(resolve, 500));
      const messagesContainer = document.querySelector(
        ".messages-container-chat",
      ) as HTMLDivElement | null;
      if (messagesContainer) {
        scrollToBottomJust(messagesContainer);
      }
      return;
    }

    if (menuToggleRef.current) {
      setRealtimeSpeechMenuAnchorEl(menuToggleRef.current);
    }
    setIsRealtimeSpeechMenuOpen(true);
  };

  return (
    <>
      <Box sx={getFooterOuterSx()}>
        <Box
          ref={realtimeSpeechAnchorRef}
          sx={getFooterShellSx(footerShellBg, isDarkMode, {
            overflowVisible: showRecordingChrome || mentionPickerOpen,
          })}
        >
          {/* {messageTheme === "lessons" && (
            <Typography
              noWrap
              sx={{
                position: "absolute",
                bottom: 0,
                left: "50%",
                transform: "translateX(-50%)",
                color: theme.palette.footer?.textInput?.color,
              }}
            >
              🔔 You are in the: "Lessons Only" mode
            </Typography>
          )} */}

          {!isMmkMode && (
            <Box sx={getFooterHintsScrollSx()}>
              <FooterQuickHints
                hidden={false}
                onSelectPrompt={(prompt) => {
                  const next = applyPromptPreservingMentions(
                    input,
                    prompt,
                    selectedMentions,
                  );
                  setInput(next);
                  textareaRef.current?.focus();
                  const pos = next.length;
                  requestAnimationFrame(() => {
                    textareaRef.current?.setSelectionRange(pos, pos);
                  });
                }}
              />
            </Box>
          )}

          <Box sx={getFooterPinnedBottomSx()}>
            {!isMmkMode && (
            <Box sx={getFooterInputRowSx(footerInputRowBg, isSmallScreen)}>
              <Box sx={getMenuToggleRowSx(isDarkMode)}>
                <Box ref={menuToggleRef}>
                  <motion.div
                    variants={bounceVariants}
                    initial="hidden"
                    animate={shouldAnimate ? "visible" : "hidden"}
                    whileHover={!isMmkMode ? "hover" : undefined}
                    onClick={isMmkMode ? undefined : toggleMenu}
                    style={{
                      cursor: isMmkMode ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      opacity: isMmkMode ? 0.5 : 1,
                    }}
                  >
                    <AppsIcon width={"24px"} height={"24px"} />
                  </motion.div>
                </Box>
              </Box>
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={getInputFormSx()}
              >
                <Box sx={getInputFormColumnSx()}>
                  <ConversationModePrompt
                    readyToSend={readyToSend}
                    validationMessage={chatSendValidationMessage}
                    onSendNew={() => void sendWithMode(false)}
                    onSendContinue={() => void sendWithMode(true)}
                  />
                  {recordedAudioUrl && !isRecording && !isTranscribing ? (
                    <VoiceRecordingPreview
                      audioUrl={recordedAudioUrl}
                      onDismiss={clearRecording}
                    />
                  ) : null}
                  <Box sx={getInputControlsRowSx()}>
                    {showAttachmentsButton && (
                      <Box sx={getAttachmentWrapperSx(isMmkMode)}>
                        <IconButton
                          disabled={isPreassessmentOpen || isMmkMode}
                          onClick={() => {
                            if (isPreassessmentOpen) return;
                            setIsPopupOpen((prev) => !prev);
                          }}
                          ref={buttonRef}
                          sx={getPaperclipButtonSx()}
                        >
                          <PaperclipIcon />
                        </IconButton>
                        {isPopupOpen && (
                          <Box ref={popupRef} sx={getAttachmentPopupSx()}>
                            <AttachmentPopup
                              isUploading={isUploading}
                              isUploadingFile={isUploadingFile}
                              handleFileSelection={handleFileSelection}
                            />
                          </Box>
                        )}
                      </Box>
                    )}

                    <ClickAwayListener
                      onClickAway={(event) => {
                        const target = event.target as HTMLElement | null;
                        if (
                          target?.closest('[data-hcp-mention-picker="true"]')
                        ) {
                          return;
                        }
                        if (mentionPickerOpen) setMentionDismissed(true);
                      }}
                    >
                      <Box sx={getInputAreaWrapperSx()}>
                        <Box
                          ref={inputSlideRef}
                          sx={getInputSlideSx(isMmkMode)}
                        >
                          {isAudioRecorderOpen && currentUser ? (
                            <WhatsAppAudioRecorder
                              embedded
                              isVisible={isAudioRecorderOpen}
                              onCancel={() => onAudioRecorderClose?.()}
                              sendMessage={setMessages}
                              sendMessageSocket={sendMessageSocket}
                              currentUser={currentUser}
                              scrollToBottom={scrollToBottom}
                            />
                          ) : showVoiceRecordingUi ? (
                            <TelegramRecordingOverlay
                              recordTimeMs={recordTimeMs}
                              clockTime={formattedClockTime}
                              cancelProgress={cancelProgress}
                              isLocked={isVoiceLocked}
                              isPaused={isPaused}
                              isCancelling={voiceGesturePhase === "cancelling"}
                              dragOffsetX={dragOffsetX}
                              pausedPreviewUrl={pausedPreviewUrl}
                              durationSeconds={Math.max(
                                0.1,
                                recordTimeMs / 1000,
                              )}
                              onCancelLocked={cancelLockedRecording}
                            />
                          ) : (
                            <Box
                              ref={mentionAnchorRef}
                              sx={getInputGradientBorderSx()}
                            >
                              <Box sx={getInputInnerSx()}>
                                <MessageTextInput
                                  ref={textareaRef}
                                  value={input}
                                  onChange={(e) => {
                                    if (isPreassessmentOpen || isMmkMode)
                                      return;
                                    setInput(e.target.value);
                                    syncTextareaCursor(e.target);
                                  }}
                                  disabled={isPreassessmentOpen || isMmkMode}
                                  onKeyDown={handleKeyDown}
                                  onKeyUp={(e) =>
                                    syncTextareaCursor(e.currentTarget)
                                  }
                                  onClick={(e) =>
                                    syncTextareaCursor(e.currentTarget)
                                  }
                                  onSelect={(e) =>
                                    syncTextareaCursor(e.currentTarget)
                                  }
                                  onInput={handleInput}
                                  gradientFrame
                                  withResponseContext
                                  placeholder={
                                    isSmallScreen
                                      ? "Type or say your question..."
                                      : "Type or say your question...🤖"
                                  }
                                />
                                <ResponseContextButton
                                  disabled={
                                    isPreassessmentOpen ||
                                    isMmkMode ||
                                    isBrowserLocked
                                  }
                                />
                              </Box>
                              <HcpMentionPicker
                                open={mentionPickerOpen}
                                anchorEl={mentionAnchorRef.current}
                                results={mentionResults}
                                query={activeMention?.query ?? ""}
                                selectedIndex={mentionIndex}
                                onHoverIndex={setMentionIndex}
                                onSelect={handleSelectMention}
                              />
                            </Box>
                          )}

                          {false && (
                            // set to `true &&` or remove `false &&` to show keyboard IconButton
                            <Box ref={keyboardToggleRef}>
                              <IconButton
                                disabled
                                aria-label="Keyboard shortcuts (inactive)"
                                sx={getKeyboardIconButtonSx(
                                  theme.palette.footer.micColor,
                                )}
                              >
                                <KeyboardIcon
                                  sx={getKeyboardIconSizeSx(isSmallScreen)}
                                />
                              </IconButton>
                            </Box>
                          )}
                        </Box>

                        {!isAudioRecorderOpen && (
                          <Box
                            sx={getSendActionContainerSx({
                              locked: isVoiceLocked,
                              recordingHold:
                                showVoiceRecordingUi && !isVoiceLocked,
                            })}
                          >
                            <Box sx={getSendActionInnerSx(isMmkMode)}>
                              {showVoiceRecordingUi && !isVoiceLocked && (
                                <Box
                                  sx={lockPillStyle(lockProgress, isDarkMode)}
                                  aria-hidden
                                >
                                  <ChevronUpIcon size={16} strokeWidth={2.5} />
                                  <LockIcon size={16} strokeWidth={2.25} />
                                </Box>
                              )}
                              {showVoiceRecordingUi && isVoiceLocked && (
                                <IconButton
                                  type="button"
                                  aria-label={
                                    isPaused
                                      ? "Resume recording"
                                      : "Pause recording"
                                  }
                                  onClick={
                                    isPaused
                                      ? handleLockedResume
                                      : handleLockedPause
                                  }
                                  sx={sendTopActionStyle()}
                                >
                                  {isPaused ? (
                                    <MicOnIcon
                                      style={{
                                        width: 16,
                                        height: 16,
                                        color: "currentColor",
                                      }}
                                    />
                                  ) : (
                                    <PauseIcon size={16} />
                                  )}
                                </IconButton>
                              )}
                              {isBrowserLocked ? (
                                <IconButton
                                  disabled={isPreassessmentOpen || isMmkMode}
                                  onClick={() => {
                                    if (isPreassessmentOpen) return;
                                    onSendMessage("/stop", false, true);
                                  }}
                                  sx={getStopButtonSx(
                                    theme.palette.footer.micColor,
                                    theme.palette.footer.micColorHover,
                                  )}
                                >
                                  <StopIcon
                                    width={isSmallScreen ? "26px" : "30px"}
                                    height={isSmallScreen ? "26px" : "30px"}
                                  />
                                </IconButton>
                              ) : (
                                <Box
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    touchAction: "none",
                                    position: "relative",
                                  }}
                                >
                                  {showVoiceRecordingUi && !isVoiceLocked ? (
                                    <MicVolumeHalo
                                      stream={voiceMediaStream}
                                      active={
                                        showVoiceRecordingUi &&
                                        !isVoiceLocked &&
                                        !isPaused
                                      }
                                      buttonPx={64}
                                    />
                                  ) : null}
                                  <IconButton
                                    ref={micButtonRef}
                                    type="button"
                                    disabled={voiceDisabled && !isVoiceLocked}
                                    onContextMenu={(event) => {
                                      event.preventDefault();
                                    }}
                                    {...(isVoiceLocked
                                      ? {
                                          onClick: handleLockedSend,
                                        }
                                      : gestureHandlers)}
                                    aria-label={
                                      isTranscribing
                                        ? "Transcribing speech"
                                        : isVoiceLocked
                                          ? "Finish recording"
                                          : showVoiceRecordingUi
                                            ? "Recording — release to finish, slide left to cancel, slide up to lock"
                                            : "Hold to record voice message"
                                    }
                                    sx={
                                      isVoiceLocked
                                        ? lockedSendButtonSx(isSmallScreen)
                                        : showVoiceRecordingUi
                                          ? getRecordingMicButtonSx(
                                              isSmallScreen,
                                              {
                                                glowing: true,
                                                locked: false,
                                              },
                                            )
                                          : getMicButtonSx(isSmallScreen)
                                    }
                                  >
                                    {isTranscribing ? (
                                      <CircularProgress
                                        size={isSmallScreen ? 16 : 18}
                                        thickness={4}
                                        sx={getTranscribingMicProgressSx()}
                                      />
                                    ) : isVoiceLocked ? (
                                      <SendIcon
                                        width={isSmallScreen ? "20px" : "24px"}
                                        height={isSmallScreen ? "20px" : "24px"}
                                      />
                                    ) : (
                                      <MicOnIcon
                                        style={{
                                          width: showVoiceRecordingUi
                                            ? isSmallScreen
                                              ? 26
                                              : 30
                                            : isSmallScreen
                                              ? 16
                                              : 18,
                                          height: showVoiceRecordingUi
                                            ? isSmallScreen
                                              ? 26
                                              : 30
                                            : isSmallScreen
                                              ? 16
                                              : 18,
                                          color: "currentColor",
                                        }}
                                      />
                                    )}
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </ClickAwayListener>
                  </Box>
                </Box>
              </Box>
            </Box>
            )}
            {/* {messageTheme === "mmk" && (
            <Typography
              noWrap
              sx={{
                textAlign: "center",
                width: "100%",
                color: theme.palette.footer?.textInput?.color,
                fontSize: "11px",
                px: 2,
                py: 0.5,
              }}
            >
              {`🔔 You are in the: "Must Master Moments" mode`}
            </Typography>
          )} */}
            <Box sx={getTabStripSx(tabStripBackground, isSmallScreen)}>
              <Box
                component="div"
                role="button"
                tabIndex={tabDisabled ? -1 : 0}
                onClick={() => {
                  if (tabDisabled) return;
                  if (!guardOnlineAction()) return;
                  updateMessageTheme("lessons");
                }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (tabDisabled) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (!guardOnlineAction()) return;
                    updateMessageTheme("lessons");
                  }
                }}
                sx={getNavTabSx(navItemColor(isLessonsMode), tabDisabled)}
              >
                <Box sx={getNavTabIconWrapSx()}>
                  <MenuBookOutlinedIcon sx={getNavTabIconSx()} />
                  {lessonsToDo.length > 0 && (
                    <Box
                      component="button"
                      type="button"
                      onClick={handleLessonsBadgeClick}
                      sx={getLessonsBadgeSx()}
                    >
                      {isLessonsBadgeLoading ? (
                        <CircularProgress
                          size={10}
                          sx={getLessonsBadgeProgressSx()}
                        />
                      ) : lessonsToDo.length > 9 ? (
                        "9+"
                      ) : (
                        lessonsToDo.length
                      )}
                    </Box>
                  )}
                </Box>
                <Typography sx={getNavTabLabelSx(isLessonsMode)}>
                  Lesson
                </Typography>
              </Box>
              <Box
                component="button"
                type="button"
                disabled={tabDisabled}
                onClick={() => {
                  if (tabDisabled) return;
                  if (!guardOnlineAction()) return;
                  updateMessageTheme("both");
                }}
                sx={getNavTabSx(navItemColor(isChatMode), tabDisabled)}
              >
                {isChatMode ? (
                  <ChatBubbleIcon sx={getNavTabIconSx()} />
                ) : (
                  <ChatBubbleOutlineIcon sx={getNavTabIconSx()} />
                )}
                <Typography sx={getNavTabLabelSx(isChatMode)}>Chat</Typography>
              </Box>
              <Box
                component="button"
                type="button"
                disabled={tabDisabled}
                onClick={() => {
                  if (tabDisabled) return;
                  if (!guardOnlineAction()) return;
                  if (messageTheme === "mmk") {
                    updateMessageTheme("both");
                  } else {
                    updateMessageTheme("mmk");
                  }
                }}
                sx={getNavTabSx(
                  navItemColor(messageTheme === "mmk"),
                  tabDisabled,
                )}
              >
                <SpeedIcon sx={getNavTabIconSx()} />
                <Typography sx={getNavTabLabelSx(messageTheme === "mmk")}>
                  MMM
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <MenuDropdown
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        menuButtons={menuButtons}
        onSendMessage={(msg) => {
          if (isOffline) {
            notifyOfflineActionBlocked();
            return;
          }
          if (msg === ROLEPLAY_COMMAND) {
            setIsMenuOpen(false);
            setMenuAnchorEl(null);
            handleRealtimeSpeechClick();
          } else {
            onSendMessage(msg, false, true);
          }
        }}
        onClose={() => {
          setIsMenuOpen(false);
          setMenuAnchorEl(null);
        }}
      />
      <RealtimeSpeechMenu
        anchorEl={realtimeSpeechMenuAnchorEl}
        open={isRealtimeSpeechMenuOpen}
        onClose={handleCloseRealtimeSpeechMenu}
      />
    </>
  );
};
